import {
    VendorConfiguration,
    UnitOfMeasurement,
    RadiusConfiguration,
    FormConfiguration,
    TemplateConfiguration,
    TabConfiguration
} from "../types/VendorConfiguration";

/**
 * Interface representing the UI components that can be controlled
 */
export interface UIComponents {
    // Tab components
    tabsetGeneral: { visible: boolean };
    tabRadius: { visible: boolean };
    tabForms: { visible: boolean };
    tabTemplates: { visible: boolean };
    tabOuterForms: { visible: boolean };
    tabTree: { visible: boolean };
    tabTripData: { visible: boolean };

    // Radius components
    textboxArrivingRadius: { visible: boolean; caption?: string };
    textboxArrivedRadius: { visible: boolean; caption?: string };
    textboxDepartRadius: { visible: boolean; caption?: string };

    panelDrvrTechWeb: { visible: boolean };
    panelPeopleNet: { visible: boolean };
    panelQCWorkflow: { visible: boolean };
    panelPlatformScience: { visible: boolean };
    panelPegasus: { visible: boolean };
    panelBluetree: { visible: boolean };
    panelSymphony: { visible: boolean };

    // UOM labels
    labelArrivingRadiusUOM: { visible: boolean; text?: string; rowBreak?: boolean };
    labelArrivedRadiusUOM: { visible: boolean; text?: string; rowBreak?: boolean };
    labelDepartRadiusUOM: { visible: boolean; text?: string; rowBreak?: boolean };

    // Form components
    textboxShipperForm: { visible: boolean, caption?: string };
    textboxStopForm: { visible: boolean, caption?: string };
    textboxDriverLoginForm: { visible: boolean };
    textboxConsigneeForm: { visible: boolean, caption?: string };
    textboxEmptyShipperForm: { visible: boolean };
    textboxEmptyConsigneeForm: { visible: boolean };
    textboxArriveShipperForm: { visible: boolean, caption?: string };
    textboxArriveStopForm: { visible: boolean };
    textboxArriveConsigneeForm: { visible: boolean, caption?: string };
    textboxDepartShipperForm: { visible: boolean };
    textboxDepartStopForm: { visible: boolean };
    textboxDepartConsigneeForm: { visible: boolean };

    // Template components
    textboxDispatchTemplate: { visible: boolean };
    textboxShipperTemplate: { visible: boolean };
    textboxStopTemplate: { visible: boolean };
    textboxConsigneeTemplate: { visible: boolean };

    // Location field
    textboxLocationId: { text: string; enabled?: boolean };

    // Control field
    textboxMcControlId: { enabled?: boolean };

    // Generic component interface for tab iteration
    components?: Array<{ id?: string; visible: boolean }>;
}

/**
 * Manages the visibility and configuration of UI components based on vendor requirements
 */
export class ComponentVisibilityManager {
    private components: UIComponents;

    constructor(components: UIComponents) {
        this.components = components;
    }

    /**
     * Applies vendor configuration to all relevant UI components
     * @param config - The vendor configuration to apply
     * @param locationId - The current location ID (used for conditional logic)
     */
    applyVendorConfiguration(config: VendorConfiguration, locationId?: string): void {
        // First, hide all components
        this.hideAllComponents();

        // Apply tab visibility
        this.applyTabConfiguration(config.tabs);

        // Apply radius configuration
        this.applyRadiusConfiguration(config.radius);

        // Apply UOM configuration
        this.applyUOMConfiguration(config.uom);

        this.applyVendorPanelConfiguration(config.vendorPanel);

        // Apply form configuration
        this.applyFormConfiguration(config.forms);

        // Apply template configuration
        this.applyTemplateConfiguration(config.templates);

        // Apply special behavior
        if (config.specialBehavior) {
            this.applySpecialBehavior(config);
        }

        // Apply location-based conditional logic
        if (config.requiresLocationCheck && !locationId) {
            this.showFormAndTreeComponents();
        }

        // Handle special cases for McLeod
        if (config.specialBehavior?.alwaysShowFormAndTree) {
            this.showFormAndTreeComponents();
        }
    }

    /**
     * Hides all vendor-specific components
     */
    hideAllComponents(): void {
        // Hide tabs
        this.components.tabForms.visible = false;
        this.components.tabTemplates.visible = false;
        this.components.tabsetGeneral.visible = false;
        this.components.tabRadius.visible = false;
        this.components.tabOuterForms.visible = false;
        this.components.tabTree.visible = false;
        this.components.tabTripData.visible = false;

        // Hide radius components
        this.components.textboxArrivingRadius.visible = false;
        this.components.textboxArrivedRadius.visible = false;
        this.components.textboxDepartRadius.visible = false;

        // Hide UOM labels
        this.components.labelArrivingRadiusUOM.visible = false;
        this.components.labelArrivedRadiusUOM.visible = false;
        this.components.labelDepartRadiusUOM.visible = false;

        // Hide form components
        this.components.textboxShipperForm.visible = false;
        this.components.textboxStopForm.visible = false;
        this.components.textboxDriverLoginForm.visible = false;
        this.components.textboxConsigneeForm.visible = false;
        this.components.textboxEmptyShipperForm.visible = false;
        this.components.textboxEmptyConsigneeForm.visible = false;
        this.components.textboxArriveShipperForm.visible = false;
        this.components.textboxArriveStopForm.visible = false;
        this.components.textboxArriveConsigneeForm.visible = false;
        this.components.textboxDepartShipperForm.visible = false;
        this.components.textboxDepartStopForm.visible = false;
        this.components.textboxDepartConsigneeForm.visible = false;

        // Hide template components
        this.components.textboxDispatchTemplate.visible = false;
        this.components.textboxShipperTemplate.visible = false;
        this.components.textboxStopTemplate.visible = false;
        this.components.textboxConsigneeTemplate.visible = false;

        // Hide components in tabs, except autoTitleLabel components
        this.hideTabComponents([
            this.components.tabRadius,
            this.components.tabForms,
            this.components.tabTemplates
        ]);
    }

    /**
     * Applies tab visibility configuration
     */
    private applyTabConfiguration(tabs: TabConfiguration): void {
        this.components.tabsetGeneral.visible = tabs.general;
        this.components.tabRadius.visible = tabs.radius;
        this.components.tabForms.visible = tabs.forms;
        this.components.tabTemplates.visible = tabs.templates;
        this.components.tabOuterForms.visible = tabs.outerForms;
        this.components.tabTree.visible = tabs.tree;
        this.components.tabTripData.visible = tabs.tripData;
    }

    /**
     * Applies radius component visibility configuration
     */
    private applyRadiusConfiguration(radius: RadiusConfiguration): void {
        this.components.textboxArrivingRadius.visible = radius.arriving;
        this.components.textboxArrivedRadius.visible = radius.arrived;
        this.components.textboxDepartRadius.visible = radius.depart;
    }

    /**
     * Applies unit of measurement configuration
     */
    private applyUOMConfiguration(uom: { arriving: UnitOfMeasurement; arrived: UnitOfMeasurement; depart: UnitOfMeasurement }): void {
        // Set visibility and text for all labels
        this.setUOMLabel(this.components.labelArrivingRadiusUOM, uom.arriving);
        this.setUOMLabel(this.components.labelArrivedRadiusUOM, uom.arrived);
        this.setUOMLabel(this.components.labelDepartRadiusUOM, uom.depart);

        // Determine which label is the last visible one and set rowBreak = true for it
        const labels = [
            { component: this.components.labelDepartRadiusUOM, order: 3 },
            { component: this.components.labelArrivedRadiusUOM, order: 2 },
            { component: this.components.labelArrivingRadiusUOM, order: 1 }
        ];

        // Find the last visible label (iterating in reverse order)
        for (const label of labels) {
            if (label.component.visible) {
                label.component.rowBreak = true;
                break;
            }
        }
    }

    private applyVendorPanelConfiguration(vendorPanel: { showDrvTechWeb?: boolean,
        showPeoplenet?: boolean,
        showQCWorkflow?: boolean,
        showPlatformScience?: boolean,
        showPegasus?: boolean,
        showBluetree?: boolean,
        showSymphony?: boolean
    }): void {
        this.components.panelDrvrTechWeb.visible = vendorPanel.showDrvTechWeb || false;
        this.components.panelPeopleNet.visible = vendorPanel.showPeoplenet || false;
        this.components.panelQCWorkflow.visible = vendorPanel.showQCWorkflow || false;
        this.components.panelPlatformScience.visible = vendorPanel.showPlatformScience || false;
        this.components.panelPegasus.visible = vendorPanel.showPegasus || false;
        this.components.panelBluetree.visible = vendorPanel.showBluetree || false;
        this.components.panelSymphony.visible = vendorPanel.showSymphony || false;
    }

    /**
     * Applies form component visibility configuration
     */
    private applyFormConfiguration(forms: FormConfiguration): void {
        this.components.textboxShipperForm.visible = forms.showShipperForm;
        this.components.textboxStopForm.visible = forms.showStopForm;
        this.components.textboxDriverLoginForm.visible = forms.showDriverLoginForm || false;
        this.components.textboxConsigneeForm.visible = forms.showConsigneeForm;
        this.components.textboxEmptyShipperForm.visible = forms.showEmptyShipperForm;
        this.components.textboxEmptyConsigneeForm.visible = forms.showEmptyConsigneeForm;
        this.components.textboxArriveShipperForm.visible = forms.showArriveShipperForm;
        this.components.textboxArriveStopForm.visible = forms.showArriveStopForm;
        this.components.textboxArriveConsigneeForm.visible = forms.showArriveConsigneeForm;
        this.components.textboxDepartShipperForm.visible = forms.showDepartShipperForm;
        this.components.textboxDepartStopForm.visible = forms.showDepartStopForm;
        this.components.textboxDepartConsigneeForm.visible = forms.showDepartConsigneeForm;
    }

    /**
     * Applies template component visibility configuration
     */
    private applyTemplateConfiguration(templates: TemplateConfiguration): void {
        this.components.textboxDispatchTemplate.visible = templates.dispatchTemplate;
        this.components.textboxShipperTemplate.visible = templates.shipperTemplate;
        this.components.textboxStopTemplate.visible = templates.stopTemplate;
        this.components.textboxConsigneeTemplate.visible = templates.consigneeTemplate;
    }

    /**
     * Applies special vendor-specific behavior
     */
    private applySpecialBehavior(config: VendorConfiguration): void {
        const special = config.specialBehavior;
        if (!special) return;

        // Apply special caption for arrived radius
        if (special.arrivedRadiusCaption) {
            this.components.textboxArrivedRadius.caption = special.arrivedRadiusCaption;
        }

        if (special.arriveShipperFormCaption) {
            this.components.textboxArriveShipperForm.caption = special.arriveShipperFormCaption;
        }

        if (special.arriveConsigneeFormCaption) {
            this.components.textboxArriveConsigneeForm.caption = special.arriveConsigneeFormCaption;
        }

        if (special.shipperFormCaption) {
            this.components.textboxShipperForm.caption = special.shipperFormCaption;
        }

        if (special.stopFormCaption) {
            this.components.textboxStopForm.caption = special.stopFormCaption;
        }

        if (special.consigneeFormCaption) {
            this.components.textboxConsigneeForm.caption = special.consigneeFormCaption;
        }

        // Hide specific tabs
        if (special.hideTemplatesTab) {
            this.components.tabTemplates.visible = false;
        }
        if (special.hideFormsTab) {
            this.components.tabForms.visible = false;
        }
    }

    /**
     * Shows form and tree components for vendors that support them
     */
    private showFormAndTreeComponents(): void {
        this.components.tabForms.visible = true;
        // Tree component logic would be implemented here if needed
    }

    /**
     * Sets UOM label visibility and text
     */
    private setUOMLabel(label: { visible: boolean; text?: string; rowBreak?: boolean }, uom: UnitOfMeasurement): void {
        if (!uom) {
            label.visible = false;
            label.rowBreak = false;
        } else {
            label.visible = true;
            label.text = uom;
            label.rowBreak = false; // Reset, will be set later for the last visible label
        }
    }

    /**
     * Hides components in tabs, excluding autoTitleLabel components
     */
    private hideTabComponents(tabs: Array<any>): void {
        tabs.forEach(tab => {
            if (!tab?.components) {
                return;
            }

            tab.components.forEach((comp: any) => {
                if (this.shouldSkipComponent(comp)) {
                    return;
                }
                comp.visible = false;
            });
        });
    }

    /**
     * Determines if a component should be skipped during hide operations
     */
    private shouldSkipComponent(comp: { id?: string }): boolean {
        return comp.id !== undefined && comp.id.includes('autoTitleLabel');
    }

    /**
     * Handles update mode behavior
     */
    applyUpdateModeConfiguration(mcControlId: string): void {
        if (mcControlId) {
            this.components.textboxMcControlId.enabled = false;
            this.components.textboxLocationId.enabled = false;
        }
    }

    /**
     * Handles trip data visibility based on vendor type
     * @param isPegasus - Whether the vendor is Pegasus (trip data should be hidden for Pegasus)
     */
    handleTripDataVisibility(isPegasus: boolean): void {
        // Implementation would depend on actual trip data components
        // This is a placeholder for future trip data functionality
        if (!isPegasus) {
            // Show trip data components if enabled and not Pegasus
            // This would need to check actual trip data checkbox state
        }
    }

    /**
     * Checks if a component is currently visible
     * @param componentId - The ID of the component to check
     * @returns true if the component is visible, false otherwise
     */
    isComponentVisible(componentId: string): boolean {
        // Map component IDs to their corresponding visibility properties
        const componentMap: Record<string, boolean> = {
            // Tab components
            'tabsetGeneral': this.components.tabsetGeneral.visible,
            'tabRadius': this.components.tabRadius.visible,
            'tabForms': this.components.tabForms.visible,
            'tabTemplates': this.components.tabTemplates.visible,
            'tabOuterForms': this.components.tabOuterForms.visible,
            'tabTree': this.components.tabTree.visible,
            'tabTripData': this.components.tabTripData.visible,
            
            // Radius components
            'textboxArrivingRadius': this.components.textboxArrivingRadius.visible,
            'textboxArrivedRadius': this.components.textboxArrivedRadius.visible,
            'textboxDepartRadius': this.components.textboxDepartRadius.visible,
            
            // UOM labels
            'labelArrivingRadiusUOM': this.components.labelArrivingRadiusUOM.visible,
            'labelArrivedRadiusUOM': this.components.labelArrivedRadiusUOM.visible,
            'labelDepartRadiusUOM': this.components.labelDepartRadiusUOM.visible,
            
            // Form components
            'textboxShipperForm': this.components.textboxShipperForm.visible,
            'textboxStopForm': this.components.textboxStopForm.visible,
            'textboxConsigneeForm': this.components.textboxConsigneeForm.visible,
            'textboxEmptyShipperForm': this.components.textboxEmptyShipperForm.visible,
            'textboxEmptyConsigneeForm': this.components.textboxEmptyConsigneeForm.visible,
            'textboxArriveShipperForm': this.components.textboxArriveShipperForm.visible,
            'textboxArriveStopForm': this.components.textboxArriveStopForm.visible,
            'textboxArriveConsigneeForm': this.components.textboxArriveConsigneeForm.visible,
            'textboxDepartShipperForm': this.components.textboxDepartShipperForm.visible,
            'textboxDepartStopForm': this.components.textboxDepartStopForm.visible,
            'textboxDepartConsigneeForm': this.components.textboxDepartConsigneeForm.visible,
            
            // Template components
            'textboxDispatchTemplate': this.components.textboxDispatchTemplate.visible,
            'textboxShipperTemplate': this.components.textboxShipperTemplate.visible,
            'textboxStopTemplate': this.components.textboxStopTemplate.visible,
            'textboxConsigneeTemplate': this.components.textboxConsigneeTemplate.visible
        };

        // Return the visibility state, defaulting to false if component ID is not found
        return componentMap[componentId] ?? false;
    }
}
